import {
  BUTTON,
  FIGHTER,
  GETUP_FRAMES,
  KNOCKDOWN_FRAMES,
  KO_BOUNCED,
  reactionKind,
  TURN_FRAMES,
  type AbilityDef,
  type FighterState,
  type SuperDef,
} from "../../sim";
import { CLIP_RANGES, strikeClipFrame } from "../../content/clips";
import type { ClipName } from "./atlas";

type Range = readonly [ClipName, number, number, number?];
/** Отрезки клипов приёмов — в данных (src/content/clips.ts): их же читает мир для хода корня. */
const ABILITY_CLIPS = CLIP_RANGES as Readonly<Record<string, Range>>;

/** Клипы только для 3D (в атласах спрайтов их нет): шаг с ходом корня. */
/** Клипы только для 3D (в пиксельном атласе их нет): шаги, реакции, присед-склейка. */
export type StepClip =
  | "medium-step-forward"
  | "step-backward"
  | "stumble-backwards"
  | "hit-reaction-head"
  | "hit-reaction-body"
  | "fight-crouch";

export interface Shot {
  readonly clip: ClipName | StepClip;
  /** Доля отрезка 0..1 или номер кадра для петель. */
  readonly frame: number;
  /** Нарисовать зеркально: кувырок вперёд из клипа читается как кувырок назад. */
  readonly mirror?: boolean;
}

/** Кадр отрезка по доле u; smooth — дробный кадр (3D: поза каждый кадр мира), иначе целый (атлас спрайтов). */
const spanOf = (range: Range, u: number, smooth: boolean): Shot => {
  const raw = u * (range[2] - range[1]);
  const top = range[2] - range[1] - 1;
  return { clip: range[0], frame: range[1] + Math.min(top, Math.max(0, smooth ? raw : Math.floor(raw))) };
};

/**
 * Шаг в 3D (Джон 04.11.2026: «персонаж странно скользит, а не идёт»): фаза клипа шага — от пройденного в мире пути,
 * одна длина шага клипа на одну длину в мире — опорная стопа стоит. Длины шагов — замер хода бёдер клипов
 * (Medium Step Forward 0,92 м, Step Backward 0,69 м) в пикселях мира при росте модели 158 px.
 */
const STEP_FORWARD = { clip: "medium-step-forward", stride: 80, frames: 27 } as const;
const STEP_BACK = { clip: "step-backward", stride: 61, frames: 24 } as const;

/**
 * Реакция на удар в 3D (Джон 04.11.2026: «когда персонажа толкают… он просто ногами скользит по полу»): лёгкий — корпус
 * отшатывается (Hit Reaction), сильный — шаги назад (начало Stumble Backwards, до падения). Родной темп клипа.
 */
export function reactionShot(fighter: FighterState): Shot {
  // сильный — шаги назад; удар в голову — голова откидывается, в корпус — сгибается; клип быстрее родного: попадание
  // читается сразу (консультант 05.11.2026: «под ударами стоит в той же стойке, реакции почти не видно»)
  const kind = reactionKind(fighter);
  if (kind === "stumble") return { clip: "stumble-backwards", frame: Math.min(44, 2 + fighter.stateFrame * 0.75) };
  const clip = kind === "head" ? "hit-reaction-head" : "hit-reaction-body";
  return { clip, frame: Math.min(40, 4 + fighter.stateFrame * 0.9) };
}

export function stepShot(fighter: FighterState): Shot {
  const back = fighter.state === FIGHTER.walkBack;
  const step = back ? STEP_BACK : STEP_FORWARD;
  // путь вдоль взгляда: вперёд — растёт при шаге вперёд, назад — при шаге назад; фаза — по модулю длины шага
  const along = (fighter.x / 256) * fighter.facing * (back ? -1 : 1);
  const phase = (((along % step.stride) + step.stride) % step.stride) / step.stride;
  return { clip: step.clip, frame: phase * step.frames };
}

/** Кадр захвата движений для бойца: по состоянию мира, без своего времени — откат и съёмка видят то же. */
export function shotFor(
  fighter: FighterState,
  ability: AbilityDef | undefined,
  worldFrame: number,
  running: boolean,
  smooth = false,
): Shot {
  const span = (range: Range, u: number): Shot => spanOf(range, u, smooth);
  const cut = smooth ? (value: number): number => value : Math.round;
  const loop = (clip: ClipName | StepClip, length: number, rate = 2): Shot => ({
    clip,
    frame: smooth ? (worldFrame / rate) % length : Math.floor(worldFrame / rate) % length,
  });
  switch (fighter.state) {
    case FIGHTER.ability: {
      const range = ABILITY_CLIPS[ability?.pose ?? ""];
      if (!ability || !range) return loop("fighting-idle", 99);
      const impact = range[3];
      const share = fighter.abilityFrame / Math.max(1, ability.startup + ability.active + ability.recovery);
      // бег назад: модель бежит спиной к сопернику, разворот клипа в конце ставит её лицом
      if (ability.pose === "backRun") return { ...span(range, share), mirror: true };
      if (impact === undefined) return span(range, share);
      // 3D: темп удара — замах виден целиком, кадр удара на первый активный кадр мира (content/clips.ts)
      if (smooth) return { clip: range[0], frame: strikeClipFrame(range, ability.startup, fighter.abilityFrame) };
      // замах клипа — на старт мира, кадр удара клипа — на первый активный кадр мира, дальше — восстановление
      const frame = fighter.abilityFrame;
      if (frame < ability.startup)
        return {
          clip: range[0],
          frame: cut(range[1] + ((impact - range[1]) * frame) / Math.max(1, ability.startup)),
        };
      const after = (frame - ability.startup) / Math.max(1, ability.active + ability.recovery);
      return { clip: range[0], frame: Math.min(range[2] - 1, cut(impact + (range[2] - 1 - impact) * after)) };
    }
    case FIGHTER.walkForward:
      return running ? loop("fast-run", 15, 2) : loop("walking", 31, 2);
    case FIGHTER.walkBack:
      return loop("walking-backwards", 26, 2);
    case FIGHTER.jump: {
      // прыжок — сальто: отрезок переворота клипа удара с переворотом по ходу полёта
      const u = Math.min(1, fighter.stateFrame / 38);
      return span(fighter.vx * fighter.facing < 0 ? ["backflip", 10, 50] : ["flip-kick", 12, 46], u);
    }
    case FIGHTER.crouch:
    case FIGHTER.crouchBlock:
      // присед в стойке: таз и ноги из Crouching Idle, корпус и кулаки у лица из боевой стойки (склейка в GLB);
      // Джон 04.11.2026: «в положении сидя он как будто держит в руках невидимое оружие»
      return loop("fight-crouch", 99, 2);
    case FIGHTER.turn:
      // разворот к сопернику — через лицо к зрителю, не мгновенный флип
      return span(["quick-180-turn", 2, 26], fighter.stateFrame / TURN_FRAMES);
    case FIGHTER.block:
      // блок — движение: руки поднимаются к голове за 8 кадров и держат защиту
      return span(["center-block", 4, 15], fighter.stateFrame / 8);
    case FIGHTER.blockstun:
      // удар в блок — отдача в защите, а не реакция на попадание; держит ↓ — защита в приседе
      if ((fighter.lastBits & BUTTON.down) !== 0) return loop("fight-crouch", 99, 2);
      return span(["center-block", 12, 22], fighter.stateFrame / 12);
    case FIGHTER.hitstun:
      return span(["receiving-a-big-uppercut", 0, 26], fighter.stateFrame / 20);
    case FIGHTER.airHit:
      // от подсечки — споткнулся и кувыркнулся (Soccer Trip), падая от соперника
      if (fighter.fallKind === 1)
        return { ...span(["soccer-trip" as ClipName, 12, 48], fighter.stateFrame / 30), mirror: true };
      // с начала клипа (2), а не с 8: там тело уже откинуто на 16 px — в кадр попадания соперник ещё на месте удара
      return span(["receiving-a-big-uppercut", 2, 60], fighter.stateFrame / 50);
    case FIGHTER.thrown:
      // бросок: отрезок пары клипов тот же, что у бросающего (content/clips.ts, throwAggressor)
      return span(["throw-victim" as ClipName, 55, 115], fighter.stateFrame / 80);
    case FIGHTER.superVictim:
      // с начала клипа (2), а не с 8: там тело уже откинуто на 16 px — в кадр попадания соперник ещё на месте удара
      return span(["receiving-a-big-uppercut", 2, 60], fighter.stateFrame / 50);
    case FIGHTER.knockdown: {
      // подъём в бой: вперёд — кувырок к сопернику, иначе — кувырок назад (тот же клип зеркально) и стойка
      const u = fighter.stateFrame / KNOCKDOWN_FRAMES;
      if (fighter.running) return span(["dive-roll", 45, 108], u);
      return { ...span(["falling-to-roll", 10, 44], u), mirror: true };
    }
    case FIGHTER.ko:
      // нокаут: в полёте — тело отлетает от удара, на земле — лежит (не доской по воздуху)
      // в первом кадре нокаута тело ещё у пола, но уже летит вверх (vy < 0) — это отлёт, не лёжка
      // клип до конца (73 из 74 кадров): в последних кадрах тело ложится — к касанию пола оно уже лежит
      if (fighter.y < 0 || fighter.vy < 0) return span(["receiving-a-big-uppercut", 2, 73], koFlight(fighter));
      return span(["getting-up", 0, 40], Math.min(1, fighter.stateFrame / KNOCKDOWN_FRAMES));
    case FIGHTER.getup:
      return span(["getting-up", 70, 165], fighter.stateFrame / GETUP_FRAMES);
    case FIGHTER.superAttack:
      return span(["flying-knee-punch-combo", 0, 40], fighter.stateFrame / 30);
    default:
      return loop("fighting-idle", 99);
  }
}

/** Тяготение героя, пикселей за кадр² (у всех героев 1 — content/heroes). */
const KO_GRAVITY = 1;

/**
 * Доля полёта нокаутированного 0..1 — по высоте и скорости, а не по часам: клип отлёта приходит в позу «лёжа» ровно к
 * касанию пола (консультант 05.11.2026: «тело ещё в полуприседе над полом и падает за 4–5 кадров»). Подскок после
 * удара о пол — уже лёжа.
 */
export function koFlight(fighter: FighterState): number {
  if (fighter.fallKind === KO_BOUNCED) return 1;
  const y = fighter.y / 256;
  const vy = fighter.vy / 256;
  // y(t) = y + vy·t + g·t²/2 = 0 (вверх — минус): сколько кадров до пола
  const left = (-vy + Math.sqrt(Math.max(0, vy * vy - 2 * KO_GRAVITY * y))) / KO_GRAVITY;
  const flown = Math.max(0, fighter.stateFrame);
  return flown + left <= 0 ? 1 : Math.min(1, flown / (flown + Math.max(0, left)));
}

/**
 * «Шквал» в 3D, пока нет заставки (Джон 04.11.2026, 23:22: «я просто замираю… непонятно… где кино?»): атакующий бьёт
 * ударами серии по её расписанию — у каждого удара свой клип, кадр удара клипа ровно на кадре попадания; жертва
 * вздрагивает на каждом попадании (голова, корпус по очереди).
 */
const SUPER_WINDUP = 10;
export function superShot3d(move: SuperDef, cinematicFrame: number, role: "attacker" | "victim"): Shot {
  const strikes = [...move.hits, move.finale];
  if (role === "victim") {
    const index = strikes.reduce((last, hit, at) => (hit.frame <= cinematicFrame ? at : last), -1);
    if (index < 0) return { clip: "hit-reaction-body", frame: 0 };
    const since = cinematicFrame - (strikes[index]?.frame ?? 0);
    return { clip: index % 2 === 0 ? "hit-reaction-head" : "hit-reaction-body", frame: Math.min(40, 4 + since / 2) };
  }
  const current = strikes.find((hit) => hit.frame + 6 >= cinematicFrame) ?? move.finale;
  const range = CLIP_RANGES[current.pose];
  if (!range) return { clip: "fighting-idle", frame: 0 };
  const frame = strikeClipFrame(range, SUPER_WINDUP, cinematicFrame - (current.frame - SUPER_WINDUP));
  return { clip: range[0] as ClipName, frame };
}

/** Кадр серии суперприёма у атакующего: удары по очереди из серии рук и апперкота. */
export function superShot(cinematicFrame: number): Shot {
  const span = (range: Range, u: number): Shot => spanOf(range, u, false);
  if (cinematicFrame < 88) return { clip: "punch-combo", frame: Math.floor((cinematicFrame / 88) * 64) };
  return span(["uppercut", 0, 40], (cinematicFrame - 88) / 40);
}
